/**
 * The power of attorney's three upload areas (main, representatives, other), recorded in the
 * configuration through `useGatedUploadScope`. Each write also captures whether the organisation requires
 * a representative's power of attorney, which completeness reads.
 */

import { useEffect } from 'react'

import type { CartItemId } from 'models/purchaseFlowRevamp'

import {
  useGatedUploadScope,
  usePoaMainUpload,
  usePoaOtherUpload,
  usePoaRepsUpload,
  useSessionDocumentsDropped
} from '../../fileHub'
import { usePowerOfAttorneyConfig } from './usePowerOfAttorneyConfig'
import { usePoaRequiresRepresentative } from './usePoaRequiresRepresentative'

export function usePoaConfigController(cartItemId: CartItemId) {
  const { value, patch } = usePowerOfAttorneyConfig(cartItemId)
  const requiresRepresentative = usePoaRequiresRepresentative()

  const main = usePoaMainUpload()
  const reps = usePoaRepsUpload()
  const other = usePoaOtherUpload()

  const dropped = useSessionDocumentsDropped(cartItemId, 'power_of_attorney')

  const mainGate = useGatedUploadScope(orderLevel(main), main.filenames, optional(value.powerOfAttorneyFilename),
    (filenames) => patch({ powerOfAttorneyFilename: filenames[0], requiresRepresentative }))
  const repsGate = useGatedUploadScope(orderLevel(reps), reps.filenames, optional(value.representativePowerOfAttorneyFilename),
    (filenames) => patch({ representativePowerOfAttorneyFilename: filenames[0], requiresRepresentative }))
  const otherGate = useGatedUploadScope(orderLevel(other), other.filenames, value.otherFilenames,
    (filenames) => patch({ otherFilenames: filenames, requiresRepresentative }))

  // The organisation's answer loads after the page, so a client who uploaded first must still have it recorded.
  const touched = mainGate.touched || repsGate.touched || otherGate.touched
  useEffect(() => {
    if (touched && requiresRepresentative !== value.requiresRepresentative) patch({ requiresRepresentative })
  }, [touched, requiresRepresentative, value.requiresRepresentative, patch])

  return {
    value,
    requiresRepresentative,
    hasRestoredFiles: dropped && !touched,
    main,
    reps,
    other,
    uploadMain: mainGate.upload,
    deleteMain: mainGate.remove,
    uploadReps: repsGate.upload,
    deleteReps: repsGate.remove,
    uploadOther: otherGate.upload,
    deleteOther: otherGate.remove,
  }
}

const optional = (filename: string | undefined): string[] => (filename ? [filename] : [])

type PoaScope = ReturnType<typeof usePoaMainUpload>

/** The order-level PoA scopes carry no tag, so their interaction is recorded under the untagged key. */
const orderLevel = (scope: PoaScope) => ({
  ...scope,
  touched: scope.touchedTags.has(''),
  markTouched: () => scope.markTouched(),
})
